import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";
import {
  FaChartLine,
  FaEuroSign,
  FaFilter,
  FaShoppingCart,
  FaStar,
  FaSyncAlt,
} from "react-icons/fa";

import { getReglements } from "../../../../../services/achatsServices";

const MONTHS_FR = [
  "Août",
  "Sept.",
  "Oct.",
  "Nov.",
  "Déc.",
  "Janv.",
  "Févr.",
  "Mars",
  "Avr.",
  "Mai",
  "Juin",
  "Juil.",
];

const EXCLUDED_STATUSES = ["Annulé", "Remboursé"];

function extractArray(data) {
  if (Array.isArray(data)) return data;

  if (data && typeof data === "object") {
    if (Array.isArray(data.rows)) return data.rows;
    if (Array.isArray(data.items)) return data.items;
    if (Array.isArray(data.achats)) return data.achats;
    if (Array.isArray(data.reglements)) return data.reglements;
    if (Array.isArray(data.data)) return data.data;
  }

  return [];
}

function toAmount(value) {
  const number = Number.parseFloat(value || 0);
  return Number.isFinite(number) ? number : 0;
}

function toQuantity(value) {
  const number = Number.parseInt(value || 0, 10);
  return Number.isFinite(number) && number > 0 ? number : 1;
}

function formatEUR(value) {
  return toAmount(value).toLocaleString("fr-FR", {
    style: "currency",
    currency: "EUR",
  });
}

function isInvalidZeroDate(value) {
  if (!value) return true;

  const raw = String(value).trim();

  return (
    raw === "0000-00-00" ||
    raw === "0000-00-00 00:00:00" ||
    raw.startsWith("0000-00-00")
  );
}

function parseValidDate(value) {
  if (isInvalidZeroDate(value)) return null;

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return null;

  return date;
}

function getSeasonBounds(season) {
  const [startYear, endYear] = String(season || "")
    .split("-")
    .map(Number);

  if (!startYear || !endYear) return null;

  return {
    startYear,
    endYear,
    start: new Date(startYear, 7, 1, 0, 0, 0, 0),
    end: new Date(endYear, 6, 31, 23, 59, 59, 999),
  };
}

function getMonthKey(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");

  return `${year}-${month}`;
}

function buildSeasonMonths(season) {
  const bounds = getSeasonBounds(season);

  if (!bounds) return [];

  const months = [];

  for (let index = 0; index < 12; index += 1) {
    const date = new Date(bounds.startYear, 7 + index, 1);

    months.push({
      key: getMonthKey(date),
      label: MONTHS_FR[index],
      fullLabel: date.toLocaleDateString("fr-FR", {
        month: "long",
        year: "numeric",
      }),
      purchases: 0,
      quantity: 0,
      amount: 0,
    });
  }

  return months;
}

function isInsideSeason(date, season) {
  const bounds = getSeasonBounds(season);

  if (!bounds) return false;

  return date >= bounds.start && date <= bounds.end;
}

function shouldCountPurchase(achat) {
  const status = String(achat?.statut_reglement || "").trim();

  if (EXCLUDED_STATUSES.includes(status)) return false;

  const date = parseValidDate(achat?.date_achat);

  if (!date) return false;

  return true;
}

function getPurchaseTypeLabel(type) {
  const value = String(type || "").trim();

  if (!value) return "Non renseigné";

  return value;
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;

  const current = payload[0]?.payload;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3 text-sm shadow-xl dark:border-slate-700 dark:bg-slate-900">
      <p className="font-bold text-slate-900 dark:text-white">
        {current?.fullLabel || label}
      </p>

      <p className="mt-2 text-slate-600 dark:text-slate-300">
        Achats :{" "}
        <span className="font-bold text-slate-900 dark:text-white">
          {current?.purchases || 0}
        </span>
      </p>

      <p className="text-slate-600 dark:text-slate-300">
        Quantités :{" "}
        <span className="font-bold text-slate-900 dark:text-white">
          {current?.quantity || 0}
        </span>
      </p>

      <p className="text-slate-600 dark:text-slate-300">
        Montant :{" "}
        <span className="font-bold text-slate-900 dark:text-white">
          {formatEUR(current?.amount || 0)}
        </span>
      </p>
    </div>
  );
}

function StatBadge({ icon, label, value, hint }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-900/70">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-[#013E8E] shadow-sm dark:bg-slate-950 dark:text-indigo-300">
          {icon}
        </span>

        <div>
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
            {label}
          </p>

          <p className="mt-1 text-lg font-bold text-slate-900 dark:text-white">
            {value}
          </p>

          {hint ? (
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              {hint}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-3">
        {[...Array(3)].map((_, index) => (
          <div
            key={index}
            className="h-24 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
          />
        ))}
      </div>

      <div className="h-[330px] animate-pulse rounded-[24px] bg-slate-100 dark:bg-slate-800" />
    </div>
  );
}

function EmptyState({ selectedSeason }) {
  return (
    <div className="rounded-[24px] border border-dashed border-slate-300 bg-slate-50 p-6 text-center dark:border-slate-700 dark:bg-slate-950/50">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
        <FaChartLine />
      </div>

      <h3 className="mt-3 text-base font-bold text-slate-900 dark:text-white">
        Aucun achat sur cette saison
      </h3>

      <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
        Aucun achat valide n’a été trouvé pour la saison {selectedSeason}.
      </p>
    </div>
  );
}

export default function MonthlyPurchasesChart({
  selectedSeason,
  seasons = [],
  onSeasonChange,
}) {
  const [achats, setAchats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [typeFilter, setTypeFilter] = useState("all");

  const fetchAchats = async () => {
    try {
      setLoading(true);
      setErrorMsg("");

      const response = await getReglements();
      const list = extractArray(response);

      setAchats(list);
    } catch (error) {
      console.error("Erreur chargement achats mensuels :", error);
      setAchats([]);
      setErrorMsg("Impossible de récupérer les achats.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAchats();
  }, []);

  const purchaseTypes = useMemo(() => {
    const values = new Set();

    achats.forEach((achat) => {
      values.add(getPurchaseTypeLabel(achat?.type_achat));
    });

    return Array.from(values).sort((a, b) => a.localeCompare(b, "fr"));
  }, [achats]);

  const filteredAchats = useMemo(() => {
    return achats.filter((achat) => {
      if (!shouldCountPurchase(achat)) return false;

      const date = parseValidDate(achat?.date_achat);

      if (!date) return false;

      if (!isInsideSeason(date, selectedSeason)) return false;

      if (typeFilter !== "all") {
        return getPurchaseTypeLabel(achat?.type_achat) === typeFilter;
      }

      return true;
    });
  }, [achats, selectedSeason, typeFilter]);

  const chartData = useMemo(() => {
    const months = buildSeasonMonths(selectedSeason);
    const byMonth = new Map(months.map((month) => [month.key, { ...month }]));

    for (const achat of filteredAchats) {
      const date = parseValidDate(achat?.date_achat);

      if (!date) continue;

      const key = getMonthKey(date);
      const current = byMonth.get(key);

      if (!current) continue;

      current.purchases += 1;
      current.quantity += toQuantity(achat?.quantite);
      current.amount += toAmount(achat?.prix_total);
    }

    return Array.from(byMonth.values()).map((item) => ({
      ...item,
      amount: Number(item.amount.toFixed(2)),
    }));
  }, [filteredAchats, selectedSeason]);

  const summary = useMemo(() => {
    const totalPurchases = chartData.reduce(
      (sum, item) => sum + item.purchases,
      0
    );

    const totalQuantity = chartData.reduce((sum, item) => sum + item.quantity, 0);

    const totalAmount = chartData.reduce((sum, item) => sum + item.amount, 0);

    const bestMonth = [...chartData].sort(
      (a, b) => b.purchases - a.purchases
    )[0];

    return {
      totalPurchases,
      totalQuantity,
      totalAmount,
      bestMonth:
        bestMonth && bestMonth.purchases > 0
          ? `${bestMonth.label} · ${bestMonth.purchases} achat${
              bestMonth.purchases > 1 ? "s" : ""
            }`
          : "—",
    };
  }, [chartData]);

  const hasData = summary.totalPurchases > 0;

  if (loading) {
    return <LoadingState />;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-indigo-50 text-[#013E8E] dark:bg-indigo-500/10 dark:text-indigo-300">
              <FaChartLine />
            </span>

            <div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                Achats mois par mois
              </h3>

              <p className="text-sm leading-6 text-slate-600 dark:text-slate-300">
                Courbe des achats effectués sur la saison {selectedSeason}, à
                partir de la date d’achat.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900">
            <FaFilter className="text-slate-400" />

            <select
              value={selectedSeason || ""}
              onChange={(event) => onSeasonChange?.(event.target.value)}
              className="min-w-[150px] bg-transparent text-sm font-bold text-slate-700 outline-none dark:text-slate-200"
            >
              {seasons.length === 0 ? (
                <option value={selectedSeason || ""}>
                  Saison {selectedSeason || "—"}
                </option>
              ) : (
                seasons.map((season) => (
                  <option key={season} value={season}>
                    Saison {season}
                  </option>
                ))
              )}
            </select>
          </div>

          <select
            value={typeFilter}
            onChange={(event) => setTypeFilter(event.target.value)}
            className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
          >
            <option value="all">Tous les achats</option>

            {purchaseTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={fetchAchats}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaSyncAlt className={loading ? "animate-spin" : ""} />
            Actualiser
          </button>
        </div>
      </div>

      {errorMsg ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300">
          {errorMsg}
        </div>
      ) : null}

      <div className="grid gap-3 md:grid-cols-3">
        <StatBadge
          icon={<FaShoppingCart />}
          label="Achats"
          value={summary.totalPurchases}
          hint={`${summary.totalQuantity} article${
            summary.totalQuantity > 1 ? "s" : ""
          } au total`}
        />

        <StatBadge
          icon={<FaEuroSign />}
          label="Montant"
          value={formatEUR(summary.totalAmount)}
          hint="Hors achats annulés ou remboursés"
        />

        <StatBadge
          icon={<FaStar />}
          label="Pic d’achats"
          value={summary.bestMonth}
          hint="Mois avec le plus d’achats"
        />
      </div>

      {!hasData ? (
        <EmptyState selectedSeason={selectedSeason} />
      ) : (
        <div className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950/60">
          <div className="h-[330px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{ top: 20, right: 18, left: 0, bottom: 8 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />

                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                />

                <YAxis
                  yAxisId="left"
                  allowDecimals={false}
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                />

                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tickFormatter={(value) => `${value} €`}
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                />

                <Tooltip content={<CustomTooltip />} />

                <Legend />

                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="purchases"
                  name="Nombre d’achats"
                  stroke="#013E8E"
                  strokeWidth={3}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />

                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="amount"
                  name="Montant"
                  stroke="#4CA82F"
                  strokeWidth={3}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}